import React, { useContext, useEffect, useState } from "react";

import clsx from "clsx";

import type { ButtonLinkProps } from "@app/components/button-link/button-link";
import { ButtonLink } from "@app/components/button-link/button-link";
import { LiteAppContext } from "@app/context/lite-app-context";
import type { LiteTranslatorService } from "@app/services/translator";
import type LiteUrlBuilderService from "@app/services/url-builders/lite-url-builder";
import type UrlBuilderService from "@app/services/url-builders/url-builder";

type Platform = "ios" | "android";
interface Props {
  platform: Platform;
  tracking?: ButtonLinkProps["tracking"];
  branch_link?: string; // A link to use instead of the default links to the App or Play stores
  className?: string;
}

/**
 * Custom hook to fallback to fallbackImage in case loading initImage is not successfull
 * and avoid broken image to be displayed
 *
 * @param initImage image to show
 * @param fallbackImage image to fallback to if initImage fails to display
 */
export const useFallbackOnErrorImage = (
  initImage: string,
  fallbackImage: string
): [string, () => void] => {
  const [image, setImageButton] = useState(() => initImage);

  useEffect(() => {
    setImageButton(() => initImage);
  }, [initImage]);

  const onError = () => {
    setImageButton(() => fallbackImage);
  };

  return [image, onError];
};

export const useNativeProps = (platform: Platform) => {
  const { locale, liteTranslator, urlBuilder } = useContext(LiteAppContext);

  const nativeProps = {
    ios: (
      translator: LiteTranslatorService,
      locale: string,
      urlBuilder: Pick<UrlBuilderService | LiteUrlBuilderService, "image">
    ) => ({
      imageSrc:
        urlBuilder.image(`/app-store/ios/app-store-badge-${locale}.png`) ?? "",
      defaultImage:
        urlBuilder.image("/app-store/ios/app-store-badge-en.png") ?? "",
      link: `itms-apps://apps.apple.com/${locale}/app/busbud/id569615989`,
      altText: translator.t("!alt.button.get-app.ios")
    }),
    android: (
      translator: LiteTranslatorService,
      locale: string,
      urlBuilder: Pick<UrlBuilderService | LiteUrlBuilderService, "image">
    ) => ({
      imageSrc:
        urlBuilder.image(
          `/app-store/android/google-play-badge-${locale}.png`
        ) ?? "",
      defaultImage:
        urlBuilder.image("/app-store/android/google-play-badge-en.png") ?? "",
      link: `https://play.google.com/store/apps/details?id=com.busbud.android&hl=${locale}`,
      altText: translator.t("!alt.button.get-app.android")
    })
  };

  return nativeProps[platform](liteTranslator, locale, urlBuilder);
};

export const DownloadNativeAppButton: React.FC<Props> = ({
  platform,
  tracking,
  branch_link,
  className
}) => {
  const { imageSrc, link, defaultImage, altText } = useNativeProps(platform);
  const [image, onError] = useFallbackOnErrorImage(imageSrc, defaultImage);

  return (
    <ButtonLink unStyled href={branch_link || link} new_tab tracking={tracking}>
      <img
        className={clsx("block h-400", className)}
        src={image}
        onError={onError}
        alt={altText}
        loading="lazy"
      />
    </ButtonLink>
  );
};
